
// api.ts
import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosProgressEvent, type AxiosRequestHeaders } from 'axios'
import { useCommonStore } from '@/stores/common'
import router from '@/router'

/** ===== 토큰 in 메모리(+선택적 localStorage 동기화) ===== */
let accessToken: string | null = localStorage.getItem('accessToken')
const setAccess = (t: string | null) => {
	accessToken = t
	if (t) localStorage.setItem('accessToken', t)
	else localStorage.removeItem('accessToken')
}

/** ===== refresh 단일 실행 ===== */
let refreshPromise: Promise<string | null> | null = null
const doRefresh = async (): Promise<string | null> => {
	if (!refreshPromise) {
		refreshPromise = (async () => {
			try {
				// 서버 규약에 맞게 수정
				const res = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
				const newAccess = res.data?.accessToken as string | undefined
				if (!newAccess) throw new Error('No accessToken in refresh response')
				setAccess(newAccess)
				return newAccess
			} catch (e) {
				setAccess(null)
				// refresh 실패 시 토큰만 삭제하고 null 반환 (리다이렉트는 인터셉터에서 채ㅜ리)
				return null
			} finally {
				refreshPromise = null
			}
		})()
	}
	return refreshPromise
}

/** ===== 공용 인스턴스 ===== */
const axiosInstance: AxiosInstance = axios.create({
	baseURL: '/api',
	withCredentials: true, // 쿠키 기반 refresh면 필수
})

/** 요청 인터셉터: 로딩 + Authorization 주입 */
axiosInstance.interceptors.request.use(
	config => {
		const store = useCommonStore()
		if (config.onUploadProgress) {
			store.startFileUploading()
		} else {
			store.startLoading()
		}

		accessToken = localStorage.getItem('accessToken')

		// 중앙에서만 토큰 주입(개별 axiosRequest에서는 지움)
		if (!config.headers) config.headers = {} as AxiosRequestHeaders
		if (accessToken) (config.headers as AxiosRequestHeaders).Authorization = `Bearer ${accessToken}`

		return config
	},
	err => {
		useCommonStore().endLoading()
		return Promise.reject(err)
	},
)

/** 응답 인터셉터: 만료 시 refresh → 원요청 재시도(단, 무한루프 방지) */
axiosInstance.interceptors.response.use(
	res => {
		useCommonStore().endLoading()
		const autoLogoutRaw = res.headers['x-labiter-auto-logout'] || res.config?.headers?.['x-labiter-auto-logout']
		if (autoLogoutRaw != null && String(autoLogoutRaw).trim() !== '') {
			const parsed = Number(autoLogoutRaw)
			// 서버 헤더가 문자열로 올 수 있어 숫자 변환 후 반영(기존 동작 유지)
			if (Number.isFinite(parsed) && parsed > 0) {
				useCommonStore().setAutoLogout(parsed)
			}
		}
		return res
	},
	async (error: AxiosError<any>) => {
		useCommonStore().endLoading()

		const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
		const status = error.response?.status
		const isAuthCall = original?.url?.includes('/auth/')

		// 500 에러 처리
		if (status === 500) {
			const commonStore = useCommonStore()
			commonStore.open('error', commonStore.lang.modal.title, commonStore.lang.modal.serverError, null)
			return Promise.reject(error)
		}

		const shouldTryRefresh = !isAuthCall && !original?._retry && (status === 401 || status === 419 || (error.response?.data as any)?.code === 'TOKEN_EXPIRED')

		if (!shouldTryRefresh) {
			return Promise.reject(error)
		}

		original!._retry = true

		const newAccess = await doRefresh()
		if (!newAccess) {
			// 선택: 서버 로그아웃 히트
			try {
				await axios.post('/api/v1/auth/logout', {}, { withCredentials: true })
			} catch (error) {
			}
			// 토큰 삭제 및 로그인 페이지로 리다이렉트
			setAccess(null)
			// localStorage 초기화
			localStorage.removeItem('userId')
			localStorage.removeItem('userName')
			// userInfo도 초기화
			const commonStore = useCommonStore()
			commonStore.setUserInfo({ userId: '', userName: '' }, {})
			// 사용자 활동 이벤트 리스너 정리
			if ((window as any).removeUserActivityListeners) {
				;(window as any).removeUserActivityListeners()
			}
			router.push({ name: 'SignIn' })
			return Promise.reject(error)
		}

		// 새 토큰으로 헤더 갱신 후 재시도
		original!.headers = original!.headers ?? {}
		;(original!.headers as any).Authorization = `Bearer ${newAccess}`
		return axiosInstance.request(original!)
	},
)

/** ===== 기존 axiosRequest 래퍼: Authorization는 인터셉터에 맡김 ===== */
export const axiosRequest = <T>(
	url: string,
	data: any,
	h: any = {},
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
	eventHandler: any = {},
	isNotLoading = true,
	progressFunction: (progressEvent: AxiosProgressEvent) => any = () => {},
): Promise<T> => {
	const headers: AxiosRequestHeaders = {
		...h,
		// ✅ 인터셉터에서 통일 주입하므로 여기선 건들지 않음
		// Authorization: accessToken ? `Bearer ${accessToken}` : '',
	}

	const config: AxiosRequestConfig = {
		url,
		method,
		headers,
		...eventHandler,
		...(method === 'POST' || method === 'PUT' || method === 'PATCH' ? { data } : { params: data }),
	}

	// ⚠️ 기존 플래그 이름이 헷갈리는데, 네 로직을 보니
	// "isNotLoading === true" 면 업로드 진행률을 켠다고 되어 있음.
	// 필요시 반대로 바꿔라.
	if (isNotLoading) {
		config.onUploadProgress = progressFunction
	}

	return axiosInstance(config).then((res: any) => res.data as T)
}

/** 선택: 부팅 시 localStorage → 메모리 동기화 util */
export const bootAuthFromStorage = () => {
	const saved = localStorage.getItem('accessToken')
	if (saved) setAccess(saved)
}

/** 선택: 로그인 성공 시 호출 */
export const onLoginSuccess = (token: string) => setAccess(token)

/** 선택: 명시 로그아웃 */
export const clearAuth = () => setAccess(null)

// export const forceRefreshOnce = async (): Promise<boolean> => {
// 	// 이미 갱신 중이면 그거 먼저 기다림
// 	if (refreshPromise) {
// 		const r = await refreshPromise
// 		return !!r
// 	}
// 	const r = await doRefresh() // 만료 여부와 무관하게 1회 시도
// 	return !!r
// }
